import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Input } from "../ui/input";
import { StatusBadge } from "../shared/StatusBadge";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const PAGE = 20;
const CATEGORIES = [
  { v: "all", label: "All categories" },
  { v: "customer", label: "Customer" },
  { v: "subscription", label: "Subscription" },
  { v: "payment", label: "Payment" },
  { v: "delivery", label: "Delivery" },
  { v: "route", label: "Route" },
];
const SRC_META = {
  manual: { label: "Manual", variant: "default" },
  system: { label: "Automated", variant: "info" },
  external: { label: "External", variant: "warning" },
};

function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts * 1000).toLocaleString("en-CA", {
      timeZone: "America/Edmonton", year: "numeric", month: "short",
      day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function Diff({ before, after }) {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (keys.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-0.5">
      {keys.map((k) => (
        <div key={k} className="text-xs text-text-muted">
          <span className="font-medium text-text-main">{k}</span>:{" "}
          <span className="line-through">{String(before?.[k] ?? "—")}</span>
          {" → "}
          <span className="text-text-main">{String(after?.[k] ?? "—")}</span>
        </div>
      ))}
    </div>
  );
}

export function CustomerActivityLog({ customerId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [category, setCategory] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ offset: String(offset), limit: String(PAGE) });
      if (category !== "all") params.set("category", category);
      if (dateFrom) params.set("date_from", String(Math.floor(new Date(dateFrom + "T00:00:00").getTime() / 1000)));
      if (dateTo) params.set("date_to", String(Math.floor(new Date(dateTo + "T23:59:59").getTime() / 1000)));
      const { data } = await apiClient.get(`/customers/${customerId}/activity?${params}`);
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not load activity log."));
    } finally {
      setLoading(false);
    }
  }, [customerId, offset, category, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [category, dateFrom, dateTo]);

  return (
    <div className="space-y-4" data-testid="customer-activity-log">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-text-muted">Category</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 w-44" data-testid="activity-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">From</label>
          <Input type="date" className="h-9 w-40" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="activity-date-from" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-text-muted">To</label>
          <Input type="date" className="h-9 w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="activity-date-to" />
        </div>
        {(dateFrom || dateTo || category !== "all") && (
          <Button variant="outline" size="sm" onClick={() => { setCategory("all"); setDateFrom(""); setDateTo(""); }} data-testid="activity-clear-filters">Clear</Button>
        )}
      </div>

      {error ? (
        <p className="py-8 text-center text-sm text-danger">{error}</p>
      ) : loading ? (
        <p className="py-8 text-center text-sm text-text-muted">Loading activity…</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-muted" data-testid="activity-empty">No activity recorded yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => {
            const src = SRC_META[r.source] || SRC_META.manual;
            return (
              <div key={i} className="rounded-lg border border-border bg-surface p-3" data-testid={`activity-row-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs uppercase tracking-wide text-text-muted">{r.category}</span>
                    <span className="text-sm font-semibold text-text-main">{r.action}</span>
                    <StatusBadge label={src.label} variant={src.variant} />
                  </div>
                  <span className="text-xs text-text-muted">{fmt(r.timestamp)}</span>
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  by <span className="font-medium text-text-main">{r.actor_username || "system"}</span>
                  {r.actor_id ? ` (#${r.actor_id})` : ""} · {r.actor_type}
                  {r.subscription_id ? ` · sub #${r.subscription_id}` : ""}
                  {r.payment_id ? ` · payment #${r.payment_id}` : ""}
                  {r.delivery_id ? ` · delivery #${r.delivery_id}` : ""}
                  {r.route_id ? ` · route #${r.route_id}` : ""}
                  {r.delivery_date ? ` · for ${r.delivery_date}` : ""}
                </p>
                <Diff before={r.before} after={r.after} />
                {r.reason && <p className="mt-1 text-xs italic text-text-main">Reason: {r.reason}</p>}
              </div>
            );
          })}
        </div>
      )}

      {total > PAGE && (
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} data-testid="activity-prev">Previous</Button>
          <span className="text-xs text-text-muted">{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <Button variant="outline" size="sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} data-testid="activity-next">Next</Button>
        </div>
      )}
    </div>
  );
}
